import { $getRoot, LexicalEditor, LexicalNode, ParagraphNode } from 'lexical';
import { addPrefixToOfficialId, devLog, isOfficalAddress, withHttp } from '@/utils';
import TwitterText from 'twitter-text';
import { ChangeData, Channel, MentionItem, TagItem, UrlItem } from '../types';
import { MentionNode } from '../nodes/MentionNode';
import { isValidHashtag } from '@/components/RichTextEditor/plugins/HashtagPlugin';
import { isValidMention } from '@/components/RichTextEditor/plugins/MentionsPlugin';
import { mentionUserSearch } from '@/components/RichTextEditor/hooks/useSearch';
import TwitterOverride from './twitterOverride';

export const getTextUnicodeLength = (text: string) => {
  return [...text].length;
};

// https://github.com/twitter/twitter-text/issues/369
// convert to unicode index
function fixTwitterTextIndices<T extends { indices: number[] }>(text: string, entity: T) {
  entity.indices[0] = Array.from(text.substring(0, entity.indices[0])).length;
  entity.indices[1] = Array.from(text.substring(0, entity.indices[1])).length;
  return entity;
}

export const removeLeadAndTrailBlankLineContent = async (editor: LexicalEditor) => {
  return new Promise(resolve => {
    editor.update(() => {
      const filterEmptyParagraphNodes = (nodes: LexicalNode[]) => {
        nodes.some(node => {
          if (node instanceof ParagraphNode) {
            const content = node.getTextContent().trim();

            if (content) {
              return true;
            }

            node.remove();
            return false;
          }
        });
      };

      // remove lead lines
      filterEmptyParagraphNodes($getRoot().getChildren());

      // remove trail lines
      filterEmptyParagraphNodes($getRoot().getChildren().reverse());

      return resolve(true);
    });
  });
};

export const advancedProcessRichTextContent = async (editor: LexicalEditor) => {
  await removeLeadAndTrailBlankLineContent(editor);
  const result = parserRichTextContent(editor);

  try {
    return await parserRichTextContentResult(result);
  } catch (e) {
    return result;
  }
};

const replaceStringByUnicodeRange = (s: string, start: number, end: number, substitute: string) => {
  // unicode index => utf-16 index
  return (
    s.substring(0, [...s].slice(0, start).join('').length) +
    substitute +
    s.substring([...s].slice(0, end).join('').length)
  );
};

const isEntityRangeConflict = (
  [start, end]: [number, number],
  currentRangeList: [number, number][],
) => {
  return currentRangeList.some(([mentionStart, mentionEnd]) => {
    return (
      (start >= mentionStart && start <= mentionEnd) || (end >= mentionStart && end <= mentionEnd)
    );
  });
};

const parserRichTextContentResult = async (result: {
  textContent: string;
  urlsWidthIndices: UrlItem[];
  boldRangeList: [number, number][];
  mentionsList: MentionItem[];
  slashesList: MentionItem[];
  hashtagsList: TagItem[];
}) => {
  const { textContent, urlsWidthIndices, boldRangeList, mentionsList, hashtagsList, slashesList } =
    result;
  let newTextContent = textContent;
  const currentRangeList = [
    ...urlsWidthIndices.map(({ indices }) => indices),
    ...mentionsList.map(({ indices }) => indices),
    ...hashtagsList.map(({ indices }) => indices),
    ...slashesList.map(({ indices }) => indices),
  ];

  const hashtagsListFromTwitterParser = [
    ...TwitterText.extractHashtagsWithIndices(textContent)
      .map(tag => ({
        hashtag: tag.hashtag.toLowerCase(),
        rawString: `#${tag.hashtag}`,
        indices: tag.indices,
      }))
      .map(item => fixTwitterTextIndices(textContent, item)),
    ...TwitterOverride.extractCashtagsWithIndices(textContent)
      .map(tag => ({
        hashtag: tag.cashtag.toLowerCase(),
        rawString: `$${tag.cashtag}`,
        indices: tag.indices,
      }))
      .map(item => fixTwitterTextIndices(textContent, item)),
  ];

  const mentionsListFromTwitterParser = TwitterText.extractMentionsOrListsWithIndices(
    textContent,
  ).map(item => fixTwitterTextIndices(textContent, item));

  const additionalHashtagsListFromTwitterParse = hashtagsListFromTwitterParser
    .filter(({ hashtag }) => isValidHashtag(hashtag))
    .filter(({ indices }) => {
      const [start, end] = indices;
      const isIncludeInRangeList = isEntityRangeConflict([start, end], currentRangeList);
      return !isIncludeInRangeList;
    });
  additionalHashtagsListFromTwitterParse.forEach(({ hashtag, rawString, indices }) => {
    hashtagsList.push({
      id: hashtag.toLowerCase(),
      raw_string: rawString,
      indices,
    });
    currentRangeList.push(indices);
  });

  const additionalMentionsListFromTwitterParser = mentionsListFromTwitterParser
    .filter(({ screenName }) => isValidMention(screenName))
    .filter(({ indices }) => {
      const [start, end] = indices;
      const isIncludeInRangeList = isEntityRangeConflict([start, end], currentRangeList);
      return !isIncludeInRangeList;
    });

  const mentions = await Promise.all(
    additionalMentionsListFromTwitterParser.map(({ screenName, indices }) => {
      return mentionUserSearch(screenName).then(({ mention }) => {
        if (mention && mention.raw_string.slice(1).length === screenName.length) {
          return { ...mention, indices };
        }
        return null;
      });
    }),
  );

  (mentions.filter(Boolean) as MentionItem[]).forEach(mention => {
    newTextContent = replaceStringByUnicodeRange(
      newTextContent,
      mention.indices[0],
      mention.indices[1],
      mention.raw_string,
    );

    mentionsList.push(mention);
  });

  devLog('parserRichTextContentResult ', {
    hashtagsListFromTwitterParser,
    additionalHashtagsListFromTwitterParse,
    mentionsListFromTwitterParser,
    additionalMentionsListFromTwitterParser,
    result,
  });

  return {
    textContent: newTextContent,
    urlsWidthIndices,
    boldRangeList,
    mentionsList: mentionsList.sort((a, b) => a.indices[0] - b.indices[0]),
    hashtagsList: hashtagsList.sort((a, b) => a.indices[0] - b.indices[0]),
    slashesList: slashesList.sort((a, b) => a.indices[0] - b.indices[0]),
  };
};

export const parserRichTextContent = (editor: LexicalEditor) => {
  const editorState = editor.getEditorState();

  return editorState.read(() => {
    const boldRangeList: [number, number][] = [];
    const mentionsList: MentionItem[] = [];
    const hashtagsList: TagItem[] = [];
    const slashesList: MentionItem[] = [];
    let current = 0;
    let textContent = '';
    let parentNode = $getRoot().getChildren()[0];

    const parserNodeText = (node: LexicalNode) => {
      const type = node.getType();
      // devLog('parserNodeText', { type, node });
      switch (type) {
        case 'text':
          const nodeText = node.getTextContent();
          // devLog('parse nodeJSON', { nodeText, node, textContent, current });
          if (node.__format === 1) {
            boldRangeList.push([current, current + getTextUnicodeLength(nodeText)]);
          }
          textContent += nodeText;
          current += getTextUnicodeLength(nodeText);

          return;
        case 'autolink':
          return node.getChildren().forEach((child: LexicalNode) => parserNodeText(child));
        case 'linebreak':
          textContent += '\n';
          current += 1; // '\n' new line length: 1
          break;
        case 'mention':
          const mentionText = node.getTextContent();
          textContent += mentionText;
          const mentionStart = current;
          current += getTextUnicodeLength(mentionText);
          const mentionData = (node as MentionNode).getData();
          mentionsList.push({
            id: isOfficalAddress(mentionData?.id)
              ? `${addPrefixToOfficialId(String(mentionData?.id))}`
              : mentionData?.id || '',
            raw_string: mentionText,
            indices: [mentionStart, current],
          });
          break;

        case 'hashtag':
          const hashtagText = node.getTextContent();
          textContent += hashtagText;
          const hashtagStart = current;
          current += getTextUnicodeLength(hashtagText);
          hashtagsList.push({
            id: hashtagText?.toLowerCase().slice(1),
            raw_string: hashtagText,
            indices: [hashtagStart, current],
          });
          break;

        case 'slash':
          const slashText = node.getTextContent();
          textContent += slashText;
          const slashStart = current;
          current += getTextUnicodeLength(slashText);
          slashesList.push({
            id: slashText?.toLowerCase().slice(1),
            raw_string: slashText,
            indices: [slashStart, current],
          });
      }
    };

    while (parentNode) {
      let node = parentNode.getFirstChild();
      // devLog(
      //   'parent paragraph Node',
      //   { parentNode, firstChild: node },
      //   parentNode.getChildren()
      // );

      while (node) {
        parserNodeText(node);
        node = node.getNextSibling();
      }

      parentNode = parentNode.getNextSibling() as LexicalNode;
      if (parentNode) {
        // has next line
        textContent += '\n';
        current += 1; // '\n' new line length: 1
      }
    }

    const urlsWidthIndices = getUrlsWithIndicesFromTextContent(textContent)
      .map(item => ({
        url: item.url,
        display_url: item.url,
        raw_string: item.url,
        indices: item.indices,
      }))
      .map(item => fixTwitterTextIndices(textContent, item));

    devLog('parserRichTextContent', {
      textContent,
      boldRangeList,
      mentionsList,
      hashtagsList,
      urlsWidthIndices,
    });

    return {
      textContent,
      urlsWidthIndices,
      boldRangeList,
      mentionsList,
      hashtagsList,
      slashesList,
    };
  });
};

// Known issue
// 1. double \n bug: https://github.com/facebook/lexical/issues/3934
// 2. extra '\n' when set bold format
export const getImpreciseTextContentFromNode = (root: LexicalNode) => {
  return root.getTextContent();
};

export const getUrlsFromTextContent = (contentStr: string) => {
  return TwitterText.extractUrls(contentStr, {
    extractUrlsWithoutProtocol: true,
  });
};

export const getUrlsWithIndicesFromTextContent = (
  contentStr: string,
): {
  url: string;
  indices: [number, number];
}[] => {
  return TwitterText.extractUrlsWithIndices(contentStr, {
    extractUrlsWithoutProtocol: true,
  });
};

export const extractUrlsIndices = (contentStr: string) => {
  return TwitterText.extractUrlsWithIndices(contentStr, {
    extractUrlsWithoutProtocol: true,
  });
};

// known issue: 【autolink escapes when typing second . 】
// https://github.com/facebook/lexical/issues/3546
export const AUTO_LINK_PLUGIN_MATCHER = [
  (text: string) => {
    const urls = getUrlsFromTextContent(text);
    const reg = new RegExp(urls.join('|'), 'g');
    const match = urls.length ? reg.exec(text) : null;

    if (match === null) {
      return null;
    }

    const fullMatch = match[0];
    return {
      index: match.index,
      length: fullMatch.length,
      text: fullMatch,
      url: withHttp(fullMatch),
      attributes: { rel: 'noopener', target: '_blank' }, // Optional link attributes
    };
  },
];

export const getCurrentEditorContent = (editor: LexicalEditor) => {
  const editorState = editor.getEditorState();
  return new Promise<ChangeData>(resolve => {
    editorState.read(async () => {
      const content = await advancedProcessRichTextContent(editor);
      resolve(content);
    });
  });
};
